<template>
  <ModalComponent
    name="emission-factor-picker"
    :shift-y="0.1"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    classes="p-6"
    @before-close="emissionFactorModalClosed"
    @opened="modalOpened"
  >
    <div class="flex flex-col gap-3">
      <div
        class="flex items-center justify-between border-b border-[#EDEDED] pb-4"
      >
        <div class="gap-1.5">
          <h3 class="text-lg font-semibold text-primary lg:text-xl">
            Select emission factor
          </h3>
          <p class="text-sm text-primary/50">
            Please select the most appropriate emission factor
          </p>
        </div>
        <button
          class="flex h-12 w-12 items-center justify-center rounded-md bg-[#EEEFF0D4] text-[#808080] outline-none"
          @click="closeEmissionFactorPicker"
        >
          <TimesSmall />
        </button>
      </div>
      <div class="flex flex-col gap-2 border-b border-[#EDEDED] pb-4">
        <h5 class="font-semibold">
          Selected emission factor
        </h5>
        <button
          v-if="selectedEmissionFactor"
          class="bg-main-primary p-3 text-left text-sm font-medium text-white no-localize-translate"
        >
          {{ selectedEmissionFactor.emission_factor }}
        </button>
        <div
          v-else
          class="flex w-full items-center justify-center border-2 border-dashed border-gray-400 p-4"
        >
          <p class="text-sm font-medium">
            No emission factor selected
          </p>
        </div>
      </div>
      <div class="flex flex-col gap-2 pb-4">
        <h5 class="font-semibold">
          Suggested emission factors
        </h5>
        <div
          v-if="
            recommendedEmissionFactors && recommendedEmissionFactors.length > 0
          "
          class="flex flex-col gap-2 pb-4"
        >
          <button
            v-for="ef in filteredRecommendedEmissionFactors"
            :key="ef.ef_id"
            class="border-2 border-main-primary p-3 text-left text-sm font-medium text-neutral-700 hover:bg-main-primary hover:text-white no-localize-translate"
            @click.prevent="emissionFactorSelected(ef)"
          >
            {{ ef.emission_factor }}
          </button>
        </div>
        <div
          v-else
          class="flex w-full items-center justify-center border-2 border-dashed border-gray-400 p-4"
        >
          <p class="text-sm font-medium">
            No recommended emission factors
          </p>
        </div>
      </div>
      <div class="flex flex-col gap-2 pb-4">
        <h5 class="font-semibold">
          Search emission factors
        </h5>
        <InputWithIcon
          placeholder="Search..."
          :val="searchQuery"
          @input="inputChanged($event)"
        >
          <template #icon>
            <SearchIcon v-if="!searching" />
            <AnimatedCircleLoader v-else />
          </template>
        </InputWithIcon>
        <div
          :ref="`ef-search-scroll-area-${uid}`"
          class="flex max-h-64 flex-col gap-1 overflow-y-auto"
        >
          <button
            v-for="ef in efSearchResults"
            :key="ef.ef_id"
            class="border-2 border-main-primary p-3 text-left text-sm font-medium text-neutral-700 hover:bg-main-primary hover:text-white no-localize-translate"
            @click.prevent="emissionFactorSelected(ef)"
          >
            {{ ef.emission_factor }}
          </button>
          <div
            v-if="searching"
            class="flex items-center justify-center py-2"
          >
            <AnimatedCircleLoader />
          </div>
          <!-- if not searching, and there's a search query, but no result, show empty state -->
          <span
            :id="`hidden-element-${uid}`"
            class="h-2"
          >&nbsp;</span>
          <div
            v-if="!searching && searchQuery && !efSearchResults.length"
            class="flex items-center justify-center py-2"
          >
            <p class="text-sm font-medium">
              No results found
            </p>
            <span
              :id="`hidden-element-${uid}`"
              class="h-2"
            >&nbsp;</span>
          </div>
        </div>
      </div>
    </div>
  </ModalComponent>
</template>

<script>
export default {
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    selectedEmissionFactor: {
      type: Object,
      default: () => {
        return {}
      },
    },
    recommendedEmissionFactors: {
      type: Array,
      default: () => {
        return []
      },
    },
    rowIndex: {
      type: Number,
      default: 0,
    },
    categoryId: {
      type: Number,
      default: 0,
    },
    selectedRowUnit: {
      type: String,
      default: '',
    },
  },

  emits: ['emission-factor-selected', 'emission-factor-picker-closed'],

  data() {
    return {
      uid: null,
      searchQuery: '',
      searching: false,
      page: 1,
      efSearchResults: [],
      hasNext: false,
      observer: null,
    }
  },

  computed: {
    filteredRecommendedEmissionFactors() {
      if (this.selectedEmissionFactor) {
        return this.recommendedEmissionFactors.filter((ef) => {
          return ef.id !== this.selectedEmissionFactor.ef_id
        })
      }
      else {
        return this.recommendedEmissionFactors
      }
    },
  },

  watch: {
    searchQuery() {
      this.resetModalData(true)
      this.performSearch()
    },
    page() {
      this.performSearch()
    },
    isOpen(val) {
      if (val) {
        this.resetModalData()
        this.$modal.show('emission-factor-picker')
      }
    },
  },
  mounted() {
    this.uid = this.$.uid
    this.observer = new IntersectionObserver(this.onElementObserved, {
      root: this.$refs[`ef-search-scroll-area-${this.uid}`],
      threshold: 1,
    })
  },
  beforeUnmount() {
    this.observer.disconnect()
  },
  methods: {
    resetModalData(fromSearch = false) {
      if (!fromSearch) {
        this.searchQuery = ''
      }
      this.hasNext = false
      this.page = 1
      this.efSearchResults = []
    },
    modalOpened() {
      nextTick(() => {
        if (document.getElementById(`hidden-element-${this.uid}`)) {
          this.observer.observe(
            document.getElementById(`hidden-element-${this.uid}`),
          )
        }
      })
    },
    onElementObserved(entries) {
      const { isIntersecting } = entries[0]
      if (isIntersecting && this.hasNext) {
        this.page++
      }
    },
    performSearch() {
      if (this.searchQuery === '') return

      this.searching = true

      this.$apis.efdb
        .listOtherEmissionFactors({
          query: this.searchQuery,
          category: this.categoryId,
          unit: this.selectedRowUnit,
          page: this.page,
        })
        .then(({ data }) => {
          this.hasNext = data.to < data.count
          this.efSearchResults = [...this.efSearchResults, ...data.results]
        })
        .finally(() => (this.searching = false))
    },
    inputChanged: debounce(function (val) {
      this.searchQuery = val
    }, 700),
    emissionFactorSelected(ef) {
      this.$emit('emission-factor-selected', {
        selectedEmissionFactor: ef,
        rowIndex: this.rowIndex,
      })
      this.resetModalData()
      this.$modal.hide('emission-factor-picker')
    },
    closeEmissionFactorPicker() {
      this.$modal.hide('emission-factor-picker')
    },
    emissionFactorModalClosed() {
      this.resetModalData()
      this.$emit('emission-factor-picker-closed')
    },
  },
}
</script>

<style></style>
